<template>
  <v-date-picker
    ref="calendar"
    :value="calendarValue"
    mode="date"
    :is-range="isRange"
    :popover="{ keepVisibleOnInput: true }"
    @input="onCalendarInput"
    @popoverDidShow="onPopoverDidShow"
  >
    <template v-slot="{ inputEvents }">
      <input
        v-bind="$attrs"
        type="text"
        readonly
        :value="displayText"
        :class="inputClass"
        :style="inputStyle"
        v-on="isRange ? inputEvents.start : inputEvents"
      />
    </template>
    <template v-slot:footer>
      <div class="dtp-time-section">
        <clock-time-picker
          v-if="!isRange"
          :value="time"
          @input="onTimeInput"
          @commit="emitMerged"
        />
        <template v-else>
          <div class="dtp-range-nav">
            <button
              type="button"
              class="dtp-nav-link"
              :class="{ active: activeTarget === 'start' }"
              @click="activeTarget = 'start'"
            >&#9664; Start</button>
            <button
              type="button"
              class="dtp-nav-link"
              :class="{ active: activeTarget === 'end' }"
              @click="activeTarget = 'end'"
            >End &#9654;</button>
          </div>
          <clock-time-picker
            v-if="activeTarget === 'start'"
            :value="startTime"
            @input="onStartTimeInput"
            @commit="onStartCommit"
          />
          <clock-time-picker
            v-else
            :value="endTime"
            @input="onEndTimeInput"
            @commit="emitMerged"
          />
        </template>
      </div>
      <slot name="footer" />
    </template>
  </v-date-picker>
</template>

<script>
import dayjs from 'dayjs';
import ClockTimePicker from './ClockTimePicker.vue';

export default {
  name: 'DateTimePicker',
  components: { ClockTimePicker },
  inheritAttrs: false,
  props: {
    value: {
      type: [Date, Object],
      default: null,
    },
    isRange: {
      type: Boolean,
      default: false,
    },
    masks: {
      type: String,
      default: 'YYYY-MM-DD HH:mm',
    },
    inputClass: {
      type: [String, Array, Object],
      default: 'form-control',
    },
    inputStyle: {
      type: [String, Object],
      default: undefined,
    },
  },
  data() {
    return {
      calendarValue: null,
      time: { hours: 0, minutes: 0 },
      startTime: { hours: 0, minutes: 0 },
      endTime: { hours: 0, minutes: 0 },
      activeTarget: 'start',
      hasAutoAdvanced: false,
      suppressCalendarEcho: false,
    };
  },
  computed: {
    hasValue() {
      if (this.isRange) {
        return !!(this.value && (this.value.start || this.value.end));
      }
      return !!this.value;
    },
    displayText() {
      if (!this.hasValue) return '';
      if (this.isRange) {
        const start = this.mergeDateTime(this.calendarValue && this.calendarValue.start, this.startTime);
        const end = this.mergeDateTime(this.calendarValue && this.calendarValue.end, this.endTime);
        return `${dayjs(start).format(this.masks)} - ${dayjs(end).format(this.masks)}`;
      }
      const merged = this.mergeDateTime(this.calendarValue, this.time);
      return dayjs(merged).format(this.masks);
    },
  },
  watch: {
    value: {
      immediate: true,
      handler(val) {
        this.syncFromValue(val);
      },
    },
  },
  methods: {
    syncFromValue(val) {
      // v-calendar echoes value-prop changes back out as its own 'input'
      // event (see forceUpdateValue in v-calendar's DatePicker.vue), so
      // suppress emitMerged() for the echo caused by our own fallback date.
      this.suppressCalendarEcho = true;
      // nested nextTick: single one already lands after the echo, this adds margin
      this.$nextTick(() => {
        this.$nextTick(() => {
          this.suppressCalendarEcho = false;
        });
      });
      if (this.isRange) {
        const start = (val && val.start) || new Date();
        const end = (val && val.end) || new Date();
        this.calendarValue = { start, end };
        this.startTime = { hours: start.getHours(), minutes: start.getMinutes() };
        this.endTime = { hours: end.getHours(), minutes: end.getMinutes() };
      } else {
        const date = val || new Date();
        this.calendarValue = date;
        this.time = { hours: date.getHours(), minutes: date.getMinutes() };
      }
    },
    mergeDateTime(date, time) {
      const base = date || new Date();
      return new Date(
        base.getFullYear(),
        base.getMonth(),
        base.getDate(),
        time.hours,
        time.minutes,
        0,
        0,
      );
    },
    emitMerged() {
      if (this.isRange) {
        const start = this.mergeDateTime(this.calendarValue && this.calendarValue.start, this.startTime);
        const end = this.mergeDateTime(this.calendarValue && this.calendarValue.end, this.endTime);
        this.$emit('input', { start, end });
      } else {
        this.$emit('input', this.mergeDateTime(this.calendarValue, this.time));
      }
    },
    onCalendarInput(val) {
      this.calendarValue = val;
      if (this.suppressCalendarEcho) return;
      this.emitMerged();
    },
    onTimeInput(newTime) {
      const hourChanged = newTime.hours !== this.time.hours;
      this.time = newTime;
      if (hourChanged) this.emitMerged();
    },
    onStartTimeInput(newTime) {
      const hourChanged = newTime.hours !== this.startTime.hours;
      this.startTime = newTime;
      if (hourChanged) this.emitMerged();
    },
    onEndTimeInput(newTime) {
      const hourChanged = newTime.hours !== this.endTime.hours;
      this.endTime = newTime;
      if (hourChanged) this.emitMerged();
    },
    onStartCommit() {
      this.emitMerged();
      if (!this.hasAutoAdvanced) {
        this.hasAutoAdvanced = true;
        this.activeTarget = 'end';
      }
    },
    onPopoverDidShow() {
      if (this.isRange) {
        this.activeTarget = 'start';
        this.hasAutoAdvanced = false;
      }
    },
    hidePopover() {
      if (this.$refs.calendar && this.$refs.calendar.hidePopover) {
        this.$refs.calendar.hidePopover();
      }
    },
  },
};
</script>

<style scoped>
.dtp-time-section {
  padding: 8px;
  border-top: 1px solid #e2e2e2;
  display: flex;
  flex-direction: column;
  align-items: center;
}
.dtp-range-nav {
  display: flex;
  gap: 12px;
  margin-bottom: 6px;
}
.dtp-nav-link {
  background: transparent;
  border: none;
  color: #6c757d;
  font-size: 13px;
}
.dtp-nav-link.active {
  color: var(--mz-primary-ink);
  font-weight: 600;
}
</style>
